import React, { useState } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { extractYouTubeID } from '../../../util/string';
import { YoutubeEmbed } from '../SectionYoutubeVideoMaybe';
import PhotoLightbox from './PhotoLightbox';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconArrowHead,
  ResponsiveImage,
} from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
  lazyLoad: true,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
    ? { aspectWidth: 4, aspectHeight: 3 }
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

/**
 * A listing that leads with a video.
 *
 * The video is not a slide: it holds the top of the page and stays there. The photos are
 * underneath it as thumbnails, and picking one opens them over the whole page.
 *
 * @component
 * @param {Object} props
 * @param {string} props.videoId - The YouTube id
 * @param {Array<propTypes.image>} props.images - The listing photos
 * @param {Array<string>} props.imageVariants - Variants for the full-size photos
 * @param {Array<string>} props.thumbVariants - Variants for the thumbnails
 * @param {Function} props.onManageDisableScrolling - Locks the page behind the lightbox
 * @returns {JSX.Element} the video layout
 */
const VideoWithPhotos = props => {
  const { videoId, images, imageVariants, thumbVariants, onManageDisableScrolling } = props;
  const intl = useIntl();
  const [openAt, setOpenAt] = useState(null);

  // Same rule as the gallery below: past two photos the strip collapses to a count, so the
  // page does not fetch a row of thumbnails nobody asked for.
  const isCompactConfigured = process.env.REACT_APP_GALLERY_COMPACT_THUMBNAILS !== 'false';
  const isCompact = isCompactConfigured && images?.length > 2;
  const shown = isCompact ? images.slice(0, 2) : images || [];
  const remainingCount = isCompact ? images.length - 2 : 0;

  const thumbButton = (key, onClick, label, children) => (
    // The class is react-image-gallery's own. The strip here is not a gallery, but it is the
    // same row of thumbnails in the same place, and should not drift from it.
    <button
      key={key}
      type="button"
      className="image-gallery-thumbnail"
      onClick={onClick}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  );

  return (
    <div className={css.videoRoot}>
      <YoutubeEmbed youtubeVideoId={videoId} aspectRatio="16/9" rootClassName={css.videoHero} />

      {images?.length > 0 ? (
        // Both classes, because the scrolling is on the outer one: the inner is a plain
        // flex row, and on its own a strip wider than the column simply runs off the side
        // of it and over whatever is there. The gallery below renders the same pair.
        <div className="image-gallery-thumbnails">
          <div className="image-gallery-thumbnails-container">
            {shown.map((img, i) =>
              thumbButton(
                `photo-${i}`,
                () => setOpenAt(i),
                intl.formatMessage(
                  { id: 'ListingImageGallery.imageThumbnailAltText' },
                  { index: i + 1, count: images.length }
                ),
                <div className={css.thumbWrapper}>
                  <ResponsiveImage
                    rootClassName={css.thumbImage}
                    image={img}
                    alt=""
                    variants={thumbVariants}
                    sizes="88px"
                  />
                </div>
              )
            )}

            {isCompact
              ? thumbButton(
                  'more',
                  // Opening on the first photo the strip did not show, since that is what the
                  // count is offering to reveal.
                  () => setOpenAt(2),
                  intl.formatMessage(
                    { id: 'ListingImageGallery.viewImagesButton' },
                    { count: images.length }
                  ),
                  <div className={css.thumbWrapper}>
                    <ResponsiveImage
                      rootClassName={classNames(css.thumbImage, css.thumbMoreBgImage)}
                      image={images[1]}
                      alt=""
                      variants={thumbVariants}
                      sizes="88px"
                    />
                    <div className={css.thumbMoreOverlay}>
                      <span className={css.thumbMoreCount}>+{remainingCount}</span>
                      <span className={css.thumbMoreLabel}>
                        <FormattedMessage
                          id={
                            remainingCount === 1
                              ? 'ListingImageGallery.moreImage'
                              : 'ListingImageGallery.moreImages'
                          }
                        />
                      </span>
                    </div>
                  </div>
                )
              : null}
          </div>
        </div>
      ) : null}

      <PhotoLightbox
        images={images}
        imageVariants={imageVariants}
        startIndex={openAt ?? 0}
        isOpen={openAt !== null}
        onClose={() => setOpenAt(null)}
        onManageDisableScrolling={onManageDisableScrolling}
      />
    </div>
  );
};

/**
 * The ListingImageGallery component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<propTypes.image>} props.images - The images
 * @param {Array<string>} props.imageVariants - The image variants
 * @param {Array<string>} props.thumbnailVariants - The thumbnail variants
 * @param {string} [props.videoUrl] - A YouTube URL. Given one, the video takes the top of the
 *   page and the photos move into a lightbox; without one, nothing here changes.
 * @param {Function} [props.onManageDisableScrolling] - Locks the page behind the lightbox
 * @returns {JSX.Element} listing image gallery component
 */
const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const intl = useIntl();
  const {
    rootClassName,
    className,
    images,
    imageVariants,
    thumbnailVariants,
    videoUrl,
    onManageDisableScrolling,
  } = props;
  const thumbVariants = thumbnailVariants || imageVariants;

  // A URL that cannot be read leaves the listing with the gallery it already had, rather
  // than a dead frame where the photos used to be.
  const videoId = extractYouTubeID(videoUrl);
  if (videoId) {
    return (
      <VideoWithPhotos
        videoId={videoId}
        images={images}
        imageVariants={imageVariants}
        thumbVariants={thumbVariants}
        onManageDisableScrolling={onManageDisableScrolling}
      />
    );
  }
  // imageVariants are scaled variants.
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);

  // Check .env setting: default is compact mode unless explicitly set to 'false'
  const isCompactConfigured = process.env.REACT_APP_GALLERY_COMPACT_THUMBNAILS !== 'false';
  // In compact mode outside fullscreen, limit to 2 thumbnails + 3rd slot showing '+X images' badge
  const isCompact = isCompactConfigured && !isFullscreen && images?.length > 2;
  const remainingCount = isCompact ? images.length - 2 : 0;

  const items = (images || []).map((img, i) => {
    const isMoreSlot = isCompact && i === 2;
    const shouldHideThumbnail = isCompact && i > 2;

    return {
      // We will only use the image resource, but react-image-gallery
      // requires the `original` key from each item.
      original: '',
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbnail: shouldHideThumbnail
        ? null
        : isMoreSlot
        ? (images?.[1] || img)?.attributes?.variants?.[thumbVariants[0]]
        : img?.attributes?.variants?.[thumbVariants[0]],
      image: img,
      isMoreSlot,
      moreCount: remainingCount,
    };
  });
  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };
  const renderItem = item => {
    return (
      <AspectRatioWrapper
        width={aspectWidth || 1}
        height={aspectHeight || 1}
        className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
      >
        <div className={css.itemCentering}>
          <ResponsiveImage
            rootClassName={css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            {...imageSizesMaybe}
          />
        </div>
      </AspectRatioWrapper>
    );
  };
  const renderThumbInner = item => {
    if (item.isMoreSlot) {
      // Re-use image index 1 (the 2nd image) as the thumbnail background.
      // Since image index 1 is already downloaded for thumbnail 2, this consumes
      // ZERO extra network requests and ZERO extra Sharetribe credits!
      const bgImage = images?.[1] || item.image;
      return (
        <div className={css.thumbWrapper}>
          <ResponsiveImage
            rootClassName={classNames(css.thumbImage, css.thumbMoreBgImage)}
            image={bgImage}
            alt={item.thumbAlt}
            variants={thumbVariants}
            sizes="88px"
          />
          <div className={css.thumbMoreOverlay}>
            <span className={css.thumbMoreCount}>+{item.moreCount}</span>
            <span className={css.thumbMoreLabel}>
              <FormattedMessage
                id={
                  item.moreCount === 1
                    ? 'ListingImageGallery.moreImage'
                    : 'ListingImageGallery.moreImages'
                }
                defaultMessage={item.moreCount === 1 ? 'image' : 'images'}
              />
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className={css.thumbWrapper}>
        <ResponsiveImage
          rootClassName={css.thumbImage}
          image={item.image}
          alt={item.thumbAlt}
          variants={thumbVariants}
          sizes="88px"
        />
      </div>
    );
  };

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeft} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRight} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };
  const renderFullscreenButton = (onClick, isFullscreen) => {
    return isFullscreen ? (
      <Button
        onClick={onClick}
        rootClassName={css.close}
        title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
      >
        <span className={css.closeText}>
          <FormattedMessage id="ListingImageGallery.closeModal" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </Button>
    ) : (
      <button className={css.openFullscreen} onClick={onClick}>
        <FormattedMessage
          id="ListingImageGallery.viewImagesButton"
          values={{ count: images.length }}
        />
      </button>
    );
  };

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <ReactImageGallery
      additionalClass={classes}
      items={items}
      renderItem={renderItem}
      renderThumbInner={renderThumbInner}
      onScreenChange={onScreenChange}
      renderLeftNav={renderLeftNav}
      renderRightNav={renderRightNav}
      renderFullscreenButton={renderFullscreenButton}
      {...IMAGE_GALLERY_OPTIONS}
    />
  );
};

export default ListingImageGallery;
